import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, Clock, Building2 } from "lucide-react";

/** Phase 1 — Public external referral page.
 *  URL: /r/:token — signed JWT-lite link, 7-day expiry, excerpt-only.
 *  No auth required. Recipient sees ONLY the excerpt + question + vendor hint.
 *  Access + response is audit-logged server-side. */
const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ExternalReferral() {
  const { token } = useParams();
  const [ref, setRef] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/referrals/${token}`)
      .then(r => setRef(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Link expired or invalid"))
      .finally(() => setBusy(false));
  }, [token]);

  const send = async () => {
    if (!response.trim()) return toast.error("Response cannot be empty");
    setSending(true);
    try {
      await axios.post(`${API}/public/referrals/${token}/respond`, { response: response.trim() });
      setSent(true);
      toast.success("Response sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (busy) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-red-200 rounded-md p-6 text-center" data-testid="external-referral-error">
        <div className="text-red-700 font-bold mb-2">Link expired or invalid</div>
        <div className="text-sm text-slate-600">This referral link is no longer active. Please request a new one from the sender.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="external-referral-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded bg-[#005289] flex items-center justify-center text-white font-bold text-sm">HCD</div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Health Care District · Legal</div>
            <div className="text-lg font-bold text-slate-900">Clause Referral</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
            <Building2 className="w-3.5 h-3.5" />
            <span>{ref.vendor_hint || "External referral"}</span>
            <span className="ml-auto flex items-center gap-1"><Clock className="w-3 h-3" /> expires {new Date(ref.expires_at).toLocaleDateString()}</span>
          </div>
          <div className="text-xs text-slate-500">Referred by <b>{ref.asked_by}</b> · {new Date(ref.asked_at).toLocaleString()}</div>

          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">Question</div>
            <div className="text-base text-slate-900 font-semibold" data-testid="external-referral-question">{ref.question}</div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">Excerpt</div>
            <pre className="text-sm text-slate-800 bg-slate-50 border border-slate-100 rounded p-3 whitespace-pre-wrap font-sans"
                 data-testid="external-referral-excerpt">{ref.excerpt}</pre>
          </div>

          {sent || ref.status === "responded" ? (
            <div className="p-4 rounded bg-emerald-50 border border-emerald-200 text-sm text-emerald-800"
                 data-testid="external-referral-sent">
              Your response has been recorded and sent back to the HCD Legal team. Thank you.
            </div>
          ) : (
            <>
              <label className="text-xs block">
                <span className="font-bold text-slate-700 block mb-1">Your response</span>
                <textarea value={response} onChange={e => setResponse(e.target.value)}
                          data-testid="external-referral-response" rows={6} maxLength={8000}
                          placeholder="Type your response here…"
                          className="w-full p-3 text-sm border border-slate-200 rounded resize-none focus:border-[#005289] focus:outline-none" />
              </label>
              <button onClick={send} disabled={sending}
                      data-testid="external-referral-send"
                      className="w-full h-10 rounded text-sm font-semibold bg-[#005289] text-white flex items-center justify-center gap-1.5 disabled:opacity-50">
                <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send response"}
              </button>
            </>
          )}

          <div className="text-[10px] text-slate-400 pt-3 border-t border-slate-100">
            This is an excerpt-only view. You do not have access to the full contract, and cannot approve, redline, or advance it.
            Every access and response is recorded for audit purposes.
          </div>
        </div>
      </div>
    </div>
  );
}
